import { getType } from 'deox';
import {
  all,
  call,
  cancelled,
  delay,
  fork,
  join,
  put,
  putResolve,
  select,
  takeEvery
} from 'redux-saga/effects';
import { Scene, SceneItem, SceneType } from 'types/Scene';
import analytics from 'utils/analytics';
import {
  deleteFile,
  getFile,
  getLocalFile,
  isFileCached,
  isFileStoredLocally,
  storeFileInLocal,
  storeFileLocal
} from 'utils/fileCache';
import getMediaDuration from 'utils/getMediaDuration';
import {
  createPreprocessingTask,
  getFileSize,
  getShutterstockImageUrlById,
  getTaskById,
  getAudios,
  createStockvideoTaskV3
} from 'utils/apiWrapper';
import {
  addPhotoSidebarImageToScene,
  applyTransitionToAllScene,
  computeScenesTimeChain,
  createDefaultScene,
  createScene,
  createStoreScene,
  deleteAllScenes,
  deleteStoreScene,
  getCurrentScene,
  getCurrentSceneId,
  getScene,
  getSceneByTimelineAndScenes,
  getSceneIdByTimeline,
  getScenes,
  loadActualShutterstockUrls,
  moveScene,
  moveStoreScene,
  selectScene,
  setCurrentScene,
  setCurrentSceneByTimeline,
  setSceneTransition,
  setVideoContentFromTool,
  trimScene,
  updateScene,
  uploadFiles,
  uploadLogFile,
  uploadSceneContent
} from 'modules/scenes';
import { deleteScene } from 'modules/effects';
import { addAlert } from 'modules/alert';
import {
  DEFAULT_IMAGE_DURATION,
  DURATION_REQUIRED_FOR_TRANSITION,
  MAX_IMAGE_DURATION,
  PREPROCESSING_POLL_DELAY
} from 'constants/scenes';
import { isNil } from 'ramda';
import { BackendSettings, ProcessingTaskStatus, ProcessingTaskStatusStatusEnum } from 'api';
import { getUserKey } from 'modules/user';
import { nanoid } from 'nanoid/non-secure';
import { setCurrentTextId } from 'modules/currentTextId';
import { getActiveTool, setActiveTool } from 'modules/activeTool';
import { ActiveTool, AppState, PaidFeatures, ToolName } from 'types/AppState';
import { Transition, TransitionType } from 'types/Transitions';
import {
  getAvailableTransitions,
  getCurrentTransitionId,
  selectCurrentTransition
} from 'modules/transitions';
import { findTransitionById } from 'utils/transitions';
import { createSceneVolume, deleteSceneVolume } from 'modules/sceneVolume';
import { getReplaceState, setReplaceAvailable } from 'modules/replaceSceneContent';
import { applyTemplate } from 'modules/template';
import { getSettings } from 'modules/settings';
import { setAudio } from 'modules/audio';
import { setSeeking, setTextLayerVisible } from 'modules/canvas';
import {
  getSceneSources,
  initScenesLoader,
  removeSceneSource,
  ScenesLoader,
  setSceneSource
} from 'modules/scenesLoader';
import { noop } from 'utils/noop';
import { getSize, restoreTimeline, SavedTimeline, saveTimeline } from 'utils/sagas/scenes';
import {
  createDefaultProgress,
  downloadFromCDN,
  fileToSceneType,
  getSceneDuration,
  handleLoadingProgress,
  HandleLoadingProgress,
  LoadingProgress,
  ProgressParts,
  uploadToCDN
} from 'utils/sagas/uploading';
import { analyticsContentAdded } from 'utils/sagas/uploadingAnalytics';
import {
  handlePhotoSidebarProgress,
  handleSceneUploadingProgress
} from 'utils/sagas/uploadingProgress';
import { RatioFormatType } from 'types/RatioFormat';
import { Audio, Song } from 'types/Audio';
import { sceneMessages } from 'constants/errorMessages';
import {
  deleteSceneDownloadingProgress,
  setScenesDownloadingProgress
} from 'modules/sceneDownloadingProgress';
import { setSceneCreationProgress } from 'modules/scenesCreationProgress';
import { extensionFromString } from 'utils/formatFilename';
import { saveRevision } from 'modules/revisions';
import { Task } from 'redux-saga';
import { saveProjectFile } from 'modules/projectFles';
import { PremiumItemSource, PremiumItemSourceByType } from '../../constants';
import { getPlayer } from 'utils/PlayerContext';
import ErrorLogger from '../../components/ErrorLogger';

function* tryRemoveFromCache(url: Scene['src'] & Scene['midResSrc']) {
  if (!url) {
    return;
  }

  const scenes: Scene[] = yield select(getScenes);
  const isContentUsed = scenes.some(({ src, midResSrc }) => src === url || midResSrc === url);

  if (!isContentUsed) {
    deleteFile(url);
  }
}

function* getTransitionDuration(transitionId: Transition['id']) {
  const transitions: Transition[] = yield select(getAvailableTransitions);
  const transitionsSettings: BackendSettings & {
    fadeDuration: number;
    wipeDiagonalDuration: number;
  } = yield select(getSettings('transitions'));
  const transition = findTransitionById(transitions, transitionId);

  while (!transitionsSettings) {
    yield delay(200);
  }

  switch (transition.type) {
    case TransitionType.Shape: {
      const duration: number = yield call(getMediaDuration, transition?.preview_url, 'video');
      return duration;
    }
    case TransitionType.Fade:
      return transitionsSettings.fadeDuration;
    case TransitionType.WipeDiagonal:
      return transitionsSettings.wipeDiagonalDuration;
    default:
      return 0;
  }
}

export function* applyReplace() {
  const { isReplaceAvailable } = yield select(getReplaceState);
  if (isReplaceAvailable) {
    yield put(setReplaceAvailable(true));
  }
}

export function* preprocessingSaga(src: string, handleProgress: HandleLoadingProgress = noop) {
  let processingInfo: ProcessingTaskStatus = {
    status: ProcessingTaskStatusStatusEnum.Running
  };

  const userKey: string | null = yield select(getUserKey);

  if (isNil(userKey)) {
    throw new Error();
  }

  const taskId: string = yield call(createPreprocessingTask, src, userKey);

  while (true) {
    processingInfo = yield call(getTaskById, taskId, userKey);

    handleProgress(processingInfo.progress);

    if (processingInfo.status === ProcessingTaskStatusStatusEnum.Error) {
      throw new Error('Error while preprocessing');
    }

    if (
      processingInfo.status === ProcessingTaskStatusStatusEnum.Running ||
      processingInfo.status === ProcessingTaskStatusStatusEnum.Queued
    ) {
      yield delay(PREPROCESSING_POLL_DELAY);
    } else break;
  }

  return processingInfo.result?.url;
}

export function* addSceneContent(
  cdnSource: string | null,
  localSource: string,
  sceneType: SceneType,
  sceneId: string,
  file: File | null,
  uploaded: boolean = true,
  source?: 'fte' | 'premium' | 'empty'
) {
  const [duration, maxDuration]: [number, number] = yield call(
    getSceneDuration,
    sceneType,
    localSource,
    sceneId
  );
  if (sceneType === SceneType.VIDEO) {
    yield put(createSceneVolume(sceneId));
  }

  // TODO: move somewhere outta here?
  if (duration < DURATION_REQUIRED_FOR_TRANSITION) {
    yield put(updateScene({ transition: null }, sceneId));
  }

  const { width, height } = yield call(getSize, sceneType, localSource);

  yield call(applyReplace);

  const scene: Scene = yield select(getScene(sceneId));
  const trimStart = scene.trimStart > maxDuration ? 0 : scene.trimStart;

  yield put(
    updateScene(
      {
        src: cdnSource,
        duration: sceneType === SceneType.IMAGE ? DEFAULT_IMAGE_DURATION : duration,
        maxDuration,
        size: { width, height },
        uploaded,
        trimStart
      },
      sceneId
    )
  );

  yield put(setSceneSource(localSource, sceneId));
  const currentScene: Scene = yield select(getCurrentScene);
  getPlayer().setTime(currentScene.start);

  yield call(analyticsContentAdded, sceneType, { width, height }, file, duration, sceneId, source);
}

export function* withoutPreprocessing(
  file: File,
  sceneType: SceneType,
  sceneId: string,
  progress: LoadingProgress
) {
  const localSource: string = yield call(storeFileInLocal, file);
  yield call(addSceneContent, null, localSource, sceneType, sceneId, file, false);
  const uploadStartedTime = Date.now();
  const userKey: string | null = yield select(getUserKey);
  const cdnSource: string = yield call(
    uploadToCDN,
    file,
    userKey,
    handleLoadingProgress(progress, ProgressParts.Uploading),
    sceneType
  );
  const uploadEndedTime = Date.now();
  yield call(storeFileLocal, file, localSource, cdnSource);
  progress.taskDone = true;

  yield call([analytics, analytics.contentUpload], {
    format: extensionFromString(file.name),
    size: file.size / 1024,
    upload_time: uploadEndedTime - uploadStartedTime
  });
  analytics.videoEditItemAction({
    item: 'image',
    license: 'personal',
    itemId: sceneId,
    is_shutterstock: false
  });
  yield put(updateScene({ src: cdnSource, uploaded: true }, sceneId));
}

export function* withPreprocessing(
  file: File,
  sceneType: SceneType,
  sceneId: string,
  progress: LoadingProgress
) {
  const uploadStartedTime = Date.now();
  const userKey: string | null = yield select(getUserKey);
  const cdcSourceUnprocessed: string = yield call(
    uploadToCDN,
    file,
    userKey,
    handleLoadingProgress(progress, ProgressParts.Uploading),
    sceneType
  );
  const uploadEndedTime = Date.now();
  yield call([analytics, analytics.contentUpload], {
    format: extensionFromString(file.name),
    size: file.size / 1024,
    upload_time: uploadEndedTime - uploadStartedTime
  });
  const cdnSource: string = yield call(
    preprocessingSaga,
    cdcSourceUnprocessed,
    handleLoadingProgress(progress, ProgressParts.Preprocessing)
  );
  const downloadStartedTime = Date.now();
  const localSource: string = yield call(
    downloadFromCDN,
    cdnSource,
    handleLoadingProgress(progress, ProgressParts.Downloading)
  );
  const downloadEndedTime = Date.now();

  const size: number = yield getFileSize(cdnSource);
  analytics.contentLoad({
    size,
    format: extensionFromString(cdnSource),
    download_time: downloadEndedTime - downloadStartedTime,
    source: 'upload',
    result: 'success'
  });

  analytics.videoEditItemAction({
    item: 'video',
    license: 'personal',
    itemId: sceneId,
    is_shutterstock: false
  });
  yield call(storeFileLocal, file, localSource, cdnSource);
  yield call(addSceneContent, cdnSource, localSource, sceneType, sceneId, file, true);
  yield put(saveProjectFile());
}

export function* uploadSceneContentSaga({
  payload: { file, id }
}: ReturnType<typeof uploadSceneContent>) {
  const sceneType: SceneType = yield call(fileToSceneType, file);
  if (!sceneType) {
    yield put(addAlert(sceneMessages.title, sceneMessages.uploading));
    return;
  }
  const progress = createDefaultProgress();
  const uploadTaskId = nanoid();
  const sceneId = id ?? (yield select(getCurrentSceneId));
  yield put(removeSceneSource(sceneId));
  const needsProcessing: boolean = sceneType === SceneType.VIDEO;

  yield put(
    updateScene(
      {
        type: sceneType,
        trimStart: 0,
        src: null,
        midResSrc: null,
        processingSrc: null,
        uploaded: false,
        uploadTaskId,
        license: null,
        sourceId: null
      },
      sceneId
    )
  );
  const uploadTask: Task = yield fork(function* () {
    try {
      const fileUsed = yield call(isFileStoredLocally, file);

      if (fileUsed) {
        const [localSource, cdnSource] = yield call(getLocalFile, file);
        yield call(addSceneContent, cdnSource, localSource, sceneType, sceneId, file, true);
        progress.taskDone = true;
        return;
      }

      if (!needsProcessing) {
        yield call(withoutPreprocessing, file, sceneType, sceneId, progress);
        return;
      }

      yield call(withPreprocessing, file, sceneType, sceneId, progress);

      progress.taskDone = true;
    } catch (e) {
      yield put(
        updateScene(
          {
            type: SceneType.EMPTY,
            src: null,
            uploaded: null,
            preprocessed: null,
            duration: 0,
            maxDuration: 0
          },
          sceneId
        )
      );

      yield put(setScenesDownloadingProgress(0, sceneId));
      yield put(addAlert(sceneMessages.uploadingTitle, sceneMessages.uploading));

      progress.errorHappened = true;
    }
  });

  yield handleSceneUploadingProgress(needsProcessing, progress, sceneId, uploadTaskId, uploadTask);
}

export function* addPhotoSidebarImageToSceneSaga({
  payload: { src, photo, id, loaded }
}: ReturnType<typeof addPhotoSidebarImageToScene>) {
  if (!src) {
    yield put(addAlert(sceneMessages.notFoundTitle, sceneMessages.notFound));
    return;
  }
  if (src.indexOf('.gif') !== -1) {
    yield put(addAlert(sceneMessages.notSupportedTitle, sceneMessages.notSupported));
    return;
  }

  const prevSceneState: Scene = yield select(getCurrentScene);
  const sceneId = prevSceneState.id;
  const license = photo.license;
  const uploadTaskId = nanoid();

  const progress: LoadingProgress = {
    uploading: 0,
    downloading: 0,
    preprocessing: 0,
    errorHappened: false,
    taskDone: false
  };

  const analyticsStats = {
    downloadStartedTime: 0,
    downloadEndedTime: 0
  };

  let duration = DEFAULT_IMAGE_DURATION;

  if (prevSceneState.type === SceneType.IMAGE) {
    duration = prevSceneState.duration;
  }

  const photoType: keyof typeof PremiumItemSourceByType = photo.type || PremiumItemSource.Picsart;
  yield put(
    updateScene(
      {
        src: null,
        midResSrc: null,
        processingSrc: null,
        uploaded: null,
        preprocessed: null,
        type: SceneType.IMAGE,
        duration,
        trimStart: 0,
        maxDuration: MAX_IMAGE_DURATION,
        license,
        sourceId: id,
        source: PremiumItemSourceByType[photoType],
        uploadTaskId
      },
      sceneId
    )
  );

  const isCached: boolean = yield call(isFileCached, src);

  const task: Task = yield fork(function* () {
    try {
      yield put(setSceneSource(null, sceneId));

      analyticsStats.downloadStartedTime = Date.now();
      const localSource: string = yield call(
        downloadFromCDN,
        src,
        handleLoadingProgress(progress, ProgressParts.Downloading)
      );
      analyticsStats.downloadEndedTime = Date.now();

      loaded();

      yield call(addSceneContent, src, localSource, SceneType.IMAGE, sceneId, null, null, license);

      if (!isCached) {
        const size: number = yield getFileSize(src);
        const url = new URL(src);
        analytics.contentLoad({
          size,
          format: url.searchParams.get('fm') || extensionFromString(url.pathname),
          download_time: analyticsStats.downloadEndedTime - analyticsStats.downloadStartedTime,
          source: 'stock_images',
          result: 'success'
        });
      }

      analytics.videoEditItemAction({
        item: 'image',
        license,
        itemId: sceneId,
        is_shutterstock: photo.type === PaidFeatures.ShutterstockPhoto
      });
      progress.taskDone = true;
    } catch {
      yield put(
        updateScene({
          src: null,
          duration: 0,
          maxDuration: 0,
          sourceId: null,
          uploaded: null,
          preprocessed: null,
          license: null,
          type: SceneType.EMPTY
        })
      );
      progress.errorHappened = true;
      const isCancelled = yield cancelled();
      if (isCancelled) {
        analyticsStats.downloadEndedTime = Date.now();
        if (!isCached) {
          const size: number = yield getFileSize(src);
          const url = new URL(src);
          analytics.contentLoad({
            size,
            format: url.searchParams.get('fm') || extensionFromString(url.pathname),
            download_time: analyticsStats.downloadEndedTime - analyticsStats.downloadStartedTime,
            source: 'stock_images',
            result: 'cancel'
          });
        }
      }
    }
  });

  yield fork(handlePhotoSidebarProgress, progress, sceneId, uploadTaskId, task);
}

export function* uploadFilesSaga({ payload: files }: ReturnType<typeof uploadFiles>) {
  const scenes: Scene[] = yield select(getScenes);
  const scene: Scene = yield select(getCurrentScene);
  const indexOfScene = scenes.indexOf(scene);
  const uploads = [];
  const updatedIds = [];

  for (let i = 0; i < files.length; i++) {
    let { id } = scene;
    if (i > 0) {
      id = nanoid();
      yield putResolve(createScene(id));
      yield put(moveStoreScene(indexOfScene, scenes.length));
    }
    // @ts-ignore
    uploads.push(yield fork(uploadSceneContentSaga, uploadSceneContent(files[i], id)));
    updatedIds.push(id);
  }

  yield join(uploads);

  const newScenes: Scene[] = yield select(getScenes);
  const newSceneIds = newScenes.map(item => item.id);
  const hasUpdatedScenes = updatedIds.some(item => newSceneIds.includes(item));

  if (hasUpdatedScenes) {
    yield put(saveRevision(ToolName.Upload));
  }
}

export function* deleteSceneSaga({ payload: id }: ReturnType<typeof deleteScene>) {
  const player = getPlayer();
  const scene: Scene = yield select(getScene(id));
  const scenes: Scene[] = yield select(getScenes);
  const currentTime: number = player.time;
  const transitions: Transition[] = yield select(getAvailableTransitions);
  const sceneSources: ScenesLoader = yield select(getSceneSources);

  if (scenes.length <= 1) {
    yield put(setReplaceAvailable(false));
  }

  if (scene) {
    yield put(deleteStoreScene(id));

    if (scene.type === SceneType.VIDEO) {
      yield put(deleteSceneVolume(id));
    }

    const sceneIndex = scenes.indexOf(scene);
    const scenes_ = [...scenes];

    scenes_.splice(sceneIndex, 1);

    const scenesAfterRemove = computeScenesTimeChain(scenes_, transitions, sceneSources);

    if (
      !scene.preprocessed &&
      !scenesAfterRemove.some((scene_: Scene) => scene_.src === scene.src)
    ) {
      yield call(deleteFile, scene.src);
    }

    if (scene.type === SceneType.VIDEO) {
      yield put(deleteSceneVolume(id));
    }

    const nextScene = scenesAfterRemove[sceneIndex];
    const prevScene = scenesAfterRemove[sceneIndex - 1];

    const hasNonEmptyScenes =
      scenesAfterRemove.filter(({ type }) => type !== SceneType.EMPTY).length === 0;

    if (hasNonEmptyScenes) {
      player.setTime(0);
      yield put(setAudio(null));
    } else {
      if (currentTime > scene.start + scene.duration) {
        player.setTime(currentTime - scene.duration - 1);
      } else if (currentTime >= scene.start) {
        player.setTime(nextScene?.start ?? prevScene?.start ?? 0);
      }
      const sceneByTimeline: Scene = yield select(
        getSceneByTimelineAndScenes(player)(scenesAfterRemove)
      );
      yield put(setCurrentScene(sceneByTimeline?.id));
    }

    yield put(setCurrentTextId([]));
    yield put(deleteStoreScene(id));
    yield put(deleteSceneDownloadingProgress(id));

    yield call<any>([analytics, analytics.sceneAction], {
      action: 'delete',
      media_type: scene.type,
      item_id: id
    });
  }
  yield put(saveRevision(ToolName.Scene));
}

export function* createSceneSaga({
  payload: { id, undoable = false }
}: ReturnType<typeof createScene>) {
  const player = getPlayer();
  player.pause();
  yield put(createStoreScene(id));

  yield call([analytics, analytics.sceneAction], {
    action: 'add'
  });
  if (undoable) {
    yield put(saveRevision(ToolName.Scene));
  }
}

export function* trimSceneSaga({
  payload: { id, duration, start: trimStart }
}: ReturnType<typeof trimScene>) {
  const player = getPlayer();
  const currentTime: number = player.time;
  const scenes: Scene[] = yield select(getScenes);
  const scene: Scene = yield select(getScene(id));
  const currentTransitionId: string = yield select(getCurrentTransitionId);
  const activeTool: ActiveTool = yield select(getActiveTool);
  const nextScene = scenes[scenes.indexOf(scene) + 1];
  const sceneEndBoundary = nextScene?.start ?? scene.start + scene.duration;
  const transition: SceneItem['transition'] =
    duration >= DURATION_REQUIRED_FOR_TRANSITION
      ? { id: scene.transition.id, duration: scene.transition.duration }
      : null;

  if (
    duration < DURATION_REQUIRED_FOR_TRANSITION &&
    currentTransitionId === id &&
    activeTool === ActiveTool.Transition
  ) {
    yield put(setActiveTool(ActiveTool.None));
  }

  yield put(updateScene({ duration, trimStart, transition }, id));

  // keep the seekbar position
  if (currentTime >= sceneEndBoundary) {
    yield call(player.setTime, currentTime + duration - scene.duration);
  } else if (currentTime >= scene.start && currentTime < sceneEndBoundary) {
    yield call(player.setTime, scene.start);
  }
  yield put(saveRevision(ToolName.Trim));
}

export function* setCurrentSceneByTimelineSaga() {
  const player = getPlayer();
  const sceneIdByTimeline: string = yield select(getSceneIdByTimeline(player));
  yield put(setCurrentScene(sceneIdByTimeline));
}

export function* setSceneTransitionSaga({
  payload: { sceneId, transitionId, undoable = false }
}: ReturnType<typeof setSceneTransition>) {
  const scenes: Scene[] = yield select(getScenes);
  const scene = scenes.find(item => item.id === sceneId);

  if (scene.duration < DURATION_REQUIRED_FOR_TRANSITION) {
    return;
  }

  const savedTimeLine: SavedTimeline = yield saveTimeline();

  // we set selected transition as soon as possible to make ui update
  yield put(updateScene({ transition: { id: transitionId } }, sceneId));

  // duration calculation can take us a couple of seconds
  const duration: number = yield call(getTransitionDuration, transitionId);
  yield put(updateScene({ transition: { id: transitionId, duration } }, sceneId));

  if (undoable) {
    yield restoreTimeline(savedTimeLine);
    yield put(saveRevision(ToolName.Transition));
    yield put(setCurrentScene(sceneId));
  }
}

function* loadActualShutterstockUrlsSaga() {
  const scenes: Scene[] = yield select(getScenes);
  const userKey: string | null = yield select(getUserKey);

  if (isNil(userKey)) {
    return;
  }

  for (const scene of scenes) {
    if (scene.sourceId && scene.source === 'shutterstock') {
      yield fork(loadShutterstockImageUrl, userKey, scene);
    }
  }
}

export function* selectSceneSaga({ payload: { id, shouldSeek } }: ReturnType<typeof selectScene>) {
  const scenes: Scene[] = yield select(getScenes);
  const player = getPlayer();

  const prevScene = scenes.find((_, i) => {
    return scenes[i + 1]?.id === id;
  });

  player.pause();
  player.setTime(prevScene ? prevScene.start + prevScene.duration : 0);

  if (shouldSeek) {
    yield put(setSeeking(true));
  }

  yield put(setCurrentScene(id));

  // TextLayer.tsx setTimeout
  yield delay(4);
  yield put(setTextLayerVisible(true));
}

export function* loadShutterstockImageUrl(userKey: string, scene: Scene) {
  try {
    const src: string = yield call(getShutterstockImageUrlById, userKey, scene.sourceId);
    yield put(updateScene({ src, trimStart: 0, maxDuration: MAX_IMAGE_DURATION }, scene.id));
    return src;
  } catch (error) {
    ErrorLogger.trigger(error);
    yield put(addAlert(sceneMessages.shutterstockTitle, sceneMessages.shutterstock));
  }
}

export function* uploadLogFileSaga({
  payload: { tempScenes, tempAudio }
}: ReturnType<typeof uploadLogFile>) {
  try {
    let audio: Audio = null;
    if (tempAudio) {
      const songs: Song[] = yield call(getAudios);
      const song = songs.find(item => tempAudio.name.includes(item.songName));
      if (song) {
        audio = { ...song, duration: parseInt(song.duration, 10) * 1000, ...tempAudio };
      }
    }

    const template: Partial<AppState> = {
      scenes: { current: null, items: [] },
      ratioFormat: RatioFormatType.Youtube,
      effects: {},
      audio,
      sceneVolume: {}
    };

    // eslint-disable-next-line guard-for-in
    for (const key in tempScenes) {
      const tempScene = tempScenes[key];
      if (!isNil(tempScene.file)) {
        const newScene = createDefaultScene(tempScene.id);

        template.scenes = {
          current: tempScene.id,
          items: [
            ...template.scenes.items,
            {
              ...newScene,
              type: SceneType.VIDEO,
              trimStart: tempScene.trimStart,
              duration: tempScene.duration,
              maxDuration: 0, // I don't know how to extract it from XML
              src: null
            }
          ]
        };
      }
    }

    if (isNil(template.scenes.current)) {
      yield put(addAlert(sceneMessages.templateTitle, sceneMessages.template));
      return;
    }

    yield put(deleteAllScenes());
    yield put(applyTemplate(template));

    // eslint-disable-next-line guard-for-in
    for (const key in tempScenes) {
      const tempScene = tempScenes[key];
      if (!isNil(tempScene.file)) {
        yield put(uploadSceneContent(tempScene.file, tempScene.id));
      }
    }
  } catch (error) {
    ErrorLogger.trigger(error);
    yield put(addAlert(sceneMessages.xmlTitle, sceneMessages.xml));
  }
  yield put(saveRevision(ToolName.Upload));
}

export function* applyTransitionToAllSceneSaga({
  payload: id
}: ReturnType<typeof applyTransitionToAllScene>) {
  const scenes: Scene[] = yield select(getScenes);
  const duration: number = yield call(getTransitionDuration, id);
  const savedTimeline: SavedTimeline = yield saveTimeline();

  const filteredScenes = scenes.filter(item => item.duration > DURATION_REQUIRED_FOR_TRANSITION);
  yield all(
    filteredScenes
      .slice(0, -1)
      .map((item: Scene) => put(updateScene({ transition: { id, duration } }, item.id)))
  );

  yield restoreTimeline(savedTimeline);
  yield put(saveRevision(ToolName.Transition));
}

export function* setVideoContentFromToolSaga({
  payload: { url, bigResSrc, license, sceneId, videoId, source }
}: ReturnType<typeof setVideoContentFromTool>) {
  const prevSceneState: Scene = yield select(getScene(sceneId));
  const userKey: string | null = yield select(getUserKey);
  if (prevSceneState.stockVideoDownloadId) {
    return;
  }

  const stockVideoDownloadId = nanoid();
  let downloadingProgress = 0;
  let bigResCreateProgress = bigResSrc ? 100 : 0;

  try {
    yield put(
      updateScene(
        {
          type: SceneType.VIDEO,
          src: null,
          trimStart: 0,
          uploaded: false,
          license,
          stockVideoDownloadId,
          source,
          sourceId: videoId
        },
        sceneId
      )
    );

    yield put(removeSceneSource(sceneId));

    yield fork(function* () {
      const scene: Scene = yield select(getScene(sceneId));

      while (
        scene &&
        scene.stockVideoDownloadId === stockVideoDownloadId &&
        downloadingProgress < 100
      ) {
        yield put(
          setSceneCreationProgress((downloadingProgress + bigResCreateProgress) / 2, sceneId)
        );
        yield delay(500);
      }
    });

    if (!bigResSrc) {
      const taskId: string = yield call(createStockvideoTaskV3, videoId, userKey);
      while (bigResCreateProgress < 100 && !bigResSrc) {
        const processingInfo: ProcessingTaskStatus = yield call(getTaskById, taskId, userKey);
        if (
          processingInfo.status === ProcessingTaskStatusStatusEnum.Error ||
          (processingInfo.progress === 100 && !processingInfo?.result?.url)
        ) {
          throw new Error();
        }
        bigResCreateProgress = processingInfo.progress;
        bigResSrc = processingInfo.result.url;
        yield delay(500);
      }
    }
    const downloadStartedTime = Date.now();

    const localSrc: string = yield call(getFile, bigResSrc, {
      handleProgress: progress => {
        downloadingProgress = progress;
      }
    });
    const downloadEndedTime = Date.now();
    const duration: number = yield call(getMediaDuration, localSrc, 'video');
    const size: { width: number; height: number } = yield call(getSize, SceneType.VIDEO, localSrc);

    yield put(
      updateScene(
        {
          src: bigResSrc,
          processingSrc: url, // link to full quality stock video
          duration,
          maxDuration: duration,
          trimStart: 0,
          size,
          uploaded: true,
          stockVideoDownloadId: undefined
        },
        sceneId
      )
    );

    const currentScene: Scene = yield select(getCurrentScene);
    getPlayer().setTime(currentScene.start);

    yield put(setSceneCreationProgress(100, sceneId));
    yield put(setSceneSource(localSrc, prevSceneState.id));
    yield call(tryRemoveFromCache, prevSceneState.src);
    yield call(tryRemoveFromCache, prevSceneState.midResSrc);

    const fileSize: number = yield call(getFileSize, bigResSrc, num => num / 1024);
    analytics.contentLoad({
      size: fileSize,
      format: extensionFromString(new URL(bigResSrc).pathname),
      download_time: downloadEndedTime - downloadStartedTime,
      source: 'stock_videos',
      result: 'success'
    });
    analytics.videoEditItemAction({
      item: 'video',
      license,
      itemId: sceneId,
      is_shutterstock: source === PremiumItemSource.Shutterstock
    });
  } catch (error) {
    ErrorLogger.trigger(error);
    yield put(updateScene(prevSceneState, sceneId));
    downloadingProgress = 100;
    bigResCreateProgress = 100;
    yield put(initScenesLoader());
    yield put(addAlert(sceneMessages.stockVideoTitle, sceneMessages.stockVideo));
  }

  yield put(setReplaceAvailable(false));
  yield put(saveRevision(ToolName.StockVideos));
}

export function* moveSceneSaga({ payload: { start, end } }: ReturnType<typeof moveScene>) {
  const savedTimeline: SavedTimeline = yield saveTimeline();
  yield put(setTextLayerVisible(false));
  yield put(setActiveTool(ActiveTool.PhotoSidebar));
  yield put(moveStoreScene(start, end));
  const scenes: Scene[] = yield select(getScenes);

  const lastScene = scenes[scenes.length - 1];

  if (scenes[end]?.id === lastScene.id || scenes[start]?.id === lastScene.id) {
    yield put(setSceneTransition(null, lastScene.id));
    yield put(selectCurrentTransition(null));
  }

  yield restoreTimeline(savedTimeline);
  yield put(saveRevision(ToolName.Scene));
}

export default function* scenesSaga() {
  yield takeEvery(getType(uploadSceneContent), uploadSceneContentSaga);
  yield takeEvery(getType(addPhotoSidebarImageToScene), addPhotoSidebarImageToSceneSaga);
  yield takeEvery(getType(deleteScene), deleteSceneSaga);
  yield takeEvery(getType(createScene), createSceneSaga);
  yield takeEvery(getType(trimScene), trimSceneSaga);
  yield takeEvery(getType(uploadFiles), uploadFilesSaga);
  yield takeEvery(getType(setCurrentSceneByTimeline), setCurrentSceneByTimelineSaga);
  yield takeEvery(getType(setSceneTransition), setSceneTransitionSaga);
  yield takeEvery(getType(applyTransitionToAllScene), applyTransitionToAllSceneSaga);
  yield takeEvery(getType(loadActualShutterstockUrls), loadActualShutterstockUrlsSaga);
  yield takeEvery(getType(selectScene), selectSceneSaga);
  yield takeEvery(getType(uploadLogFile), uploadLogFileSaga);
  yield takeEvery(getType(setVideoContentFromTool), setVideoContentFromToolSaga);
  yield takeEvery(getType(moveScene), moveSceneSaga);
}
